import type { IAppleClientParams, IGoogleClientParams, IOauth, IUploadFile } from '../types';

/**
 * Google Oauth API: https://developers.google.com/identity/oauth2/web/reference/js-reference?hl=zh-cn
 * 使用指南：
 * 1. 建立项目: (https://console.cloud.google.com/)
 * 2. 开启相关服务API
 * 3. 在凭证中开启可信 JavaScript 源
 */

const loadGoogleSDK = async () => {
  return new Promise((resolve, reject) => {
    const scriptElement = document.createElement('script');
    scriptElement.id = 'googleGsiClient';
    const oldScript = document.getElementById('googleGsiClient');
    if (oldScript) {
      document.body.removeChild(oldScript);
    }
    scriptElement.src = `https://accounts.google.com/gsi/client?timestamp=${Date.now()}`;
    scriptElement.onload = resolve;
    scriptElement.onerror = () => {
      reject('time out');
    };
    document.body.append(scriptElement);
  });
};

// scopes document: https://developers.google.com/identity/protocols/oauth2/scopes
export class GoogleOauth implements IOauth {
  accessToken: string | undefined;
  expires_time: number | undefined;

  constructor() {}
  init = async (clientParams: IGoogleClientParams | IAppleClientParams) => {
    const googleParams = clientParams as IGoogleClientParams;
    const userCallback = googleParams.callback;
    googleParams.callback = (response) => {
      const { access_token } = response;
      response['expires_time'] = +new Date() + response['expires_in'] * 1000;
      this.accessToken = access_token;
      this.expires_time = response['expires_time'];
      userCallback?.(response);
    };

    if (!globalThis.google) {
      await loadGoogleSDK();
    }

    const client = (globalThis as any).google.accounts.oauth2.initTokenClient(clientParams);
    client.requestAccessToken();
  };

  requestCode = async (clientParams: IGoogleClientParams | IAppleClientParams) => {
    const googleParams = clientParams as IGoogleClientParams;
    const userCallback = googleParams.callback;
    googleParams.callback = (response) => {
      // const { access_token } = response;
      // response['expires_time'] = +new Date() + response['expires_in'] * 1000;
      // this.accessToken = access_token;
      // this.expires_time = response['expires_time'];
      userCallback?.(response);
    };

    if (!globalThis.google) {
      await loadGoogleSDK();
    }

    const client = (globalThis as any).google.accounts.oauth2.initCodeClient(clientParams);
    client.requestCode();
  };

  requestToken = async (clientParams: IGoogleClientParams | IAppleClientParams) => {
    const googleParams = clientParams as IGoogleClientParams;
    const userCallback = googleParams.callback;
    googleParams.callback = (response) => {
      const { access_token } = response;
      response['expires_time'] = +new Date() + response['expires_in'] * 1000;
      this.accessToken = access_token;
      this.expires_time = response['expires_time'];
      userCallback?.(response);
    };

    if (!globalThis.google) {
      await loadGoogleSDK();
    }

    const client = (globalThis as any).google.accounts.oauth2.initTokenClient(clientParams);
    client.requestAccessToken();
  };

  setAccessToken = (accessToken: string) => {
    this.accessToken = accessToken;
  };
  authCloudKit = () => {
    return new Promise((_, reject) => {
      reject('GoogleOauth does not support the authCloudKit method');
    });
  };
  getUserInfo = async () => {
    const serRes = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo`, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    }).then((response) => {
      if (response.status === 200) {
        return response.json();
      }
      throw Error(
        JSON.stringify({
          status: response.status,
        })
      );
    });
    return {
      email: serRes.email,
      id: serRes.sub,
      name: serRes.name,
      picture: serRes.picture,
    };
  };

  getDriveFiles = async (props: IUploadFile['metadata']) => {
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&orderBy=createdTime desc&q=name contains '${props.name}'`,
      {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      }
    ).then((response) => {
      if (response.status === 200) {
        return response.json();
      }
      throw Error(
        JSON.stringify({
          status: response.status,
        })
      );
    });
    return res;
  };

  uploadFile = async (props: IUploadFile) => {
    const { metadata, file } = props;
    const form = new FormData();
    metadata.parents = ['appDataFolder'];
    const url = `https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart`;
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);
    return fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.accessToken}` },
      body: form,
    }).then((response) => {
      if (response.status === 200) {
        return response.json();
      }
      throw Error(
        JSON.stringify({
          status: response.status,
        })
      );
    });
  };

  deleteFile = async (fileId: string) => {
    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });

    if (response.status === 204) {
      return { success: true, message: 'File deleted successfully' };
    } else {
      throw Error(
        JSON.stringify({
          status: response.status,
          message: 'Failed to delete file',
        })
      );
    }
  };

  getFileContent = async (fileId: string) => {
    const serRes = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    }).then((response) => {
      if (response.status === 200) {
        return response.json();
      }
      throw Error(
        JSON.stringify({
          status: response.status,
        })
      );
    });
    return serRes;
  };

  getQuota = async () => {
    const response = await fetch(
      'https://www.googleapis.com/drive/v3/about?fields=storageQuota',
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    ).then((response) => {
      if (response.status === 200) {
        return response.json();
      }
      throw Error(
        JSON.stringify({
          status: response.status,
        })
      );
    });
    return response;
  };

  checkQuota = async (size: number) => {
    const quota = await this.getQuota();
    const limit = Number(quota.storageQuota.limit);
    const usage = Number(quota.storageQuota.usage);
    // limit maybe "UNLIMITED"，then return true
    if (isNaN(limit)) return true;
    const free = limit - usage;
    return free > size;
  };
}
